import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../../theme';
import { Button } from '../../../../components/Button';
import { formatMoney, formatRate } from '../../../../utils';
import { useTranslation } from '../../../../hooks/useTranslation';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

const SectionTitle = ({ icon, title, colors }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Box sx={{ color: colors.greenAccent[500] }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{title}</Typography>
  </Box>
);

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '11px', color: '#9e9e9e', mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{value ?? '-'}</Typography>
  </Box>
);

const Step4Confirmacao = ({ simulation, formData, onBack, onSubmit, submitting, options }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const s = 'createContractModal.step4';

  const credor = (options?.credores ?? []).find((c) => c.id === formData.credor);
  const baseCalc = (options?.basesCalculo ?? []).find((b) => b.value === formData.baseCalculo);

  const garantiasSelecionadas = (formData.garantias ?? [])
    .filter((g) => g.value)
    .map((g) => (options?.garantias ?? []).find((gk) => gk.id === g.value)?.name)
    .filter(Boolean);

  const totalValue = simulation?.total_value ?? 150000;
  const liquidValue = simulation?.financed_value ?? 147500;
  const monthlyRate = simulation?.interest_rate ?? 2.5;
  const installmentCount = simulation?.installments ?? 6;
  const installmentValue = simulation?.installment_value ?? 25625;

  const installments = Array.from({ length: installmentCount }, (_, i) => {
    const d = new Date('2024-02-15');
    d.setMonth(d.getMonth() + i);
    return { num: i + 1, dueDate: d.toLocaleDateString('pt-BR'), value: installmentValue };
  });

  return (
    <Box>
      {/* Dados Gerais */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <SectionTitle icon={<DescriptionOutlinedIcon fontSize="small" />} title={t(`${s}.generalData`)} colors={colors} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Field label={t(`${s}.ccbId`)} value={`CCB-${new Date().getFullYear()}-${String(simulation?.id ?? '001').padStart(6, '0')}`} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label={t(`${s}.assetCode`)} value="VRTX-GRF-2026" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label={t(`${s}.ipoc`)} value="12345678901234567890" />
          </Grid>
        </Grid>
      </Box>

      {/* Credor */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <SectionTitle icon={<PersonOutlineIcon fontSize="small" />} title={t(`${s}.creditorSection`)} colors={colors} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><Field label={t(`${s}.name`)} value={credor?.name} /></Grid>
          <Grid item xs={12} md={6}><Field label={t(`${s}.cnpj`)} value="12.345.678/0001-90" /></Grid>
        </Grid>
      </Box>

      {/* Devedor */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <SectionTitle icon={<PersonOutlineIcon fontSize="small" />} title={t(`${s}.debtorSection`)} colors={colors} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><Field label={t(`${s}.name`)} value={simulation?.customer?.full_name ?? 'Carlos Eduardo Mendes'} /></Grid>
          <Grid item xs={12} md={4}><Field label={t(`${s}.cpf`)} value={simulation?.customer?.taxvar ?? '123.456.789-00'} /></Grid>
          <Grid item xs={12} md={4}><Field label={t(`${s}.email`)} value={simulation?.customer?.email ?? 'carlos.mendes@email.com'} /></Grid>
          <Grid item xs={12} md={4}><Field label={t(`${s}.phone`)} value={simulation?.customer?.cellphone ?? '(11) 98765-4321'} /></Grid>
          <Grid item xs={12} md={4}><Field label={t(`${s}.grossIncome`)} value={formatMoney(simulation?.customer?.income ?? 25000)} /></Grid>
          <Grid item xs={12} md={4}><Field label={t(`${s}.nationality`)} value="Brasileira" /></Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 1.5, color: '#555' }}>
          {t(`${s}.addressSection`)}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><Field label={t(`${s}.cep`)} value={simulation?.customer?.address?.cep ?? '01310-100'} /></Grid>
          <Grid item xs={12} md={3}><Field label={t(`${s}.city`)} value={simulation?.customer?.address?.city ?? 'São Paulo'} /></Grid>
          <Grid item xs={12} md={3}><Field label={t(`${s}.state`)} value={simulation?.customer?.address?.state ?? 'SP'} /></Grid>
          <Grid item xs={12} md={3}><Field label={t(`${s}.country`)} value="Brasil" /></Grid>
        </Grid>
      </Box>

      {/* Pagamento — 2 blocos separados */}
      <SectionTitle icon={<PaymentsOutlinedIcon fontSize="small" />} title={t(`${s}.paymentSection`)} colors={colors} />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, height: '100%' }}>
            {[
              [t(`${s}.grossValue`), formatMoney(totalValue)],
              [t(`${s}.netValue`), formatMoney(liquidValue)],
              [t(`${s}.index`), formData.indice],
              [t(`${s}.periodicity`), 'Mensal'],
              [t(`${s}.calculationBase`), baseCalc?.label],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>{label}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, height: '100%' }}>
            {[
              [t(`${s}.monthlyInterest`), formatRate(monthlyRate)],
              [t(`${s}.totalInterest`), formatMoney(38820)],
              [t(`${s}.cet`), formatRate(3.12)],
              [t(`${s}.iof`), formatMoney(1500)],
              [t(`${s}.installments`), String(installmentCount)],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>{label}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{value}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>{t(`${s}.installmentValue`)}</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.greenAccent[500] }}>
                {formatMoney(installmentValue)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Garantias */}
      {garantiasSelecionadas.length > 0 && (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
          <SectionTitle icon={<SecurityOutlinedIcon fontSize="small" />} title={t(`${s}.guaranteesSection`)} colors={colors} />
          {garantiasSelecionadas.map((g, i) => (
            <Typography key={i} sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5 }}>• {g}</Typography>
          ))}
        </Box>
      )}

      {/* Parcelas */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <SectionTitle icon={<ReceiptLongOutlinedIcon fontSize="small" />} title={t(`${s}.installmentsSection`)} colors={colors} />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  t(`${s}.columns.installment`),
                  t(`${s}.columns.dueDate`),
                  t(`${s}.columns.day`),
                  t(`${s}.columns.amortization`),
                  t(`${s}.columns.interest`),
                  t(`${s}.columns.indexer`),
                  t(`${s}.columns.value`),
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '12px', color: '#555' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {installments.map((inst, i) => (
                <TableRow key={inst.num}>
                  <TableCell sx={{ fontSize: '12px' }}>{inst.num}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{inst.dueDate}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{i === 0 ? 1 : i * 30}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{formatMoney(1495 + i * 65)}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{formatMoney(3750 - i * 50)}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>R$ 0,00</TableCell>
                  <TableCell sx={{ fontSize: '12px', color: colors.greenAccent[500], fontWeight: 600 }}>
                    {formatMoney(inst.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Rodapé */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
        <Button variant="outlined" fullWidth={false} onClick={onBack} sx={{ minWidth: 120 }}>
          {t(`${s}.backButton`)}
        </Button>
        <Button
          variant="contained"
          fullWidth={false}
          onClick={onSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DescriptionOutlinedIcon />}
          sx={{ minWidth: 180 }}
        >
          {submitting ? t(`${s}.generatingButton`) : t(`${s}.generateButton`)}
        </Button>
      </Box>
    </Box>
  );
};

export default Step4Confirmacao;
